<template>
    <b-modal
        id="setup-horizons-modal"
        size="xl"
        hide-footer
        hide-header
        centered
        :no-close-on-backdrop="isSubmitting"
        :no-close-on-esc="isSubmitting"
        @hidden="onHideModal"
    >
        <div class="setup-horizons">
            <span class="title">
                {{ $t('strategy.onepage.advancedOptions.horizons.title') }}
            </span>
            <div class="description">
                {{ $t('strategy.onepage.advancedOptions.horizons.description') }}
            </div>
            <div v-if="!isLoading">
                <b-row class="mb-3">
                    <b-col :key="onePageHorizon.index.$model" v-for="onePageHorizon in $v.onePageHorizons.$each.$iter">
                        <div class="horizon-form-group">
                            <b-form-group :id="`one-page-horizon-${onePageHorizon.index.$model}`">
                                <template slot="label">
                                    <span class="horizon-title">{{
                                        $t('strategy.onepage.advancedOptions.horizons.title') +
                                        ` ${onePageHorizon.index.$model} `
                                    }}</span>
                                </template>
                                <description-input
                                    class="mb-2"
                                    :index="onePageHorizon.index.$model"
                                    :state="checkState(onePageHorizon.description)"
                                    :value.sync="onePageHorizon.description.$model"
                                />
                                <date-range-picker
                                    :onePage="onePage"
                                    :onePageHorizon="onePageHorizon"
                                    :dateStartState="checkState(onePageHorizon.date_start)"
                                    :dateEndState="checkState(onePageHorizon.date_end)"
                                    @change="onChangeDates($event, onePageHorizon)"
                                />
                                <validations
                                    :onePage="onePage"
                                    :onePageHorizon="onePageHorizon"
                                    :state="checkState(onePageHorizon)"
                                />
                            </b-form-group>
                        </div>
                    </b-col>
                </b-row>
                <actions :isSubmitting="isSubmitting" @cancel="onClickCancel" @submit="onClickSubmit" />
            </div>
            <div class="loader-container">
                <loader :loader="isLoading" />
            </div>
        </div>
    </b-modal>
</template>
<script>
import Loader from '@/views/components/loader'
import StrategyService from '@/views/strategy/api'
import { getDateFromStringWithoutTime } from '@/lib/date'
import { validationMixin } from 'vuelidate'
import validations from './validations'
import { mapState } from 'vuex'
import { Actions, Validations, DateRangePicker, DescriptionInput } from './components'
import { isEmpty as objectIsEmpty } from 'lodash'
import { success, danger, info } from '@/lib/notification'

const strategyService = new StrategyService()

export default {
    components: {
        Loader,
        Actions,
        Validations,
        DateRangePicker,
        DescriptionInput,
    },
    mixins: [validationMixin],
    validations,
    props: {
        onePageId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            isLoading: false,
            isSubmitting: false,
            onePageHorizons: [],
        }
    },
    computed: {
        ...mapState('strategy', {
            onePage: (state) => state.onepage,
        }),
        formIsInvalid() {
            return Object.keys(this.$v.onePageHorizons.$each.$iter).reduce((acc, key) => {
                const fieldState = this.checkState(this.$v.onePageHorizons.$each.$iter[key])
                const itemIsInvalid = fieldState === false
                return acc || itemIsInvalid
            }, false)
        },
    },
    mounted() {
        this.setOnePageHorizons()
        this.openModal()
    },
    methods: {
        async onClickSubmit() {
            try {
                const payload = this.getParsedFormToApi()

                if (this.formIsInvalid) {
                    return
                }

                if (objectIsEmpty(payload)) {
                    return this.showInfoNotification()
                }

                this.isSubmitting = true

                await this.updateHorizons(payload)

                this.showSuccessNotification()
                this.$emit('updateHorizons')
                this.closeModal()
            } catch (e) {
                console.error(e)
                this.showErrorNotification()
            } finally {
                this.isSubmitting = false
            }
        },
        getParsedFormToApi() {
            const data = {}

            Object.keys(this.$v.onePageHorizons.$each.$iter).forEach((key) => {
                const onePageHorizon = this.$v.onePageHorizons.$each.$iter[key]

                if (onePageHorizon.$anyDirty) {
                    onePageHorizon.$touch()

                    if (!onePageHorizon.$invalid) {
                        const model = onePageHorizon.$model

                        data[model.index] = { ...model }
                    }
                }
            })

            return data
        },
        showInfoNotification() {
            info(this.$t('strategy.onepage.horizons.modal.info'))
        },
        showSuccessNotification() {
            success(this.$t('strategy.onepage.horizons.modal.success'))
        },
        showErrorNotification() {
            danger()
        },
        updateHorizons(payload) {
            return strategyService.updateHorizons(this.onePageId, {
                horizons: payload,
            })
        },
        onChangeDates(dates, onePageHorizon) {
            onePageHorizon.date_start.$model = dates ? dates.start : null
            onePageHorizon.date_end.$model = dates ? dates.end : null
        },
        async setOnePageHorizons() {
            if (!this.onePageId) {
                return
            }

            this.isLoading = true

            const onePageHorizons = await strategyService.getOnePageHorizons(this.onePageId)

            this.onePageHorizons = this.parseOnePageHorizonsToForm(onePageHorizons)

            this.isLoading = false
        },
        parseOnePageHorizonsToForm({ data }) {
            return data.map((item) => {
                const { date_start, date_end, description, horizon } = item
                return {
                    index: horizon.type.substr(1),
                    description,
                    date_start: getDateFromStringWithoutTime(date_start, 'YYYY-MM-DD'),
                    date_end: getDateFromStringWithoutTime(date_end, 'YYYY-MM-DD'),
                }
            })
        },
        checkState(field) {
            return field.$dirty ? !field.$invalid : null
        },
        onClickCancel() {
            this.closeModal()
        },
        closeModal() {
            this.$bvModal.hide('setup-horizons-modal')
        },
        onHideModal() {
            this.$router.go(-1)
        },
        openModal() {
            this.$bvModal.show('setup-horizons-modal')
        },
    },
    watch: {
        onePageId() {
            this.setOnePageHorizons()
        },
    },
}
</script>
<style lang="scss" scoped src="./styles.scss"></style>
